import { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../contexts/AuthProvider";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { isHeadNurse } from "../../../../utils/utilFunctions";
import { useHolidayGroups, useHolidays } from "../holidayQueries";
import { type ScheduleHolidays, type ScheduleRange, toScheduleHolidays } from "./scheduleHolidays";

/** Holidays are read through head-nurse-only endpoints, so staff (and admins in staff view) never ask. */
export const useCanSeeScheduleHolidays = (): boolean => {
	const { userInfo } = useAuth();
	return userInfo.support || isHeadNurse(userInfo.role);
};

const ISO_DATE = "yyyy-MM-dd";

// DEFAULT: with no week loaded the query is off, so this only fills its key; nothing is asked for it.
const UNLOADED_DAY = DateTime.fromMillis(0);

/**
 * The department's holidays for the schedule's loaded days. No request until a week has loaded, or ever for users who
 * can't see them. Call it once per page (`ScheduleHolidaysProvider`): a failed load is tracked once per call.
 */
export const useScheduleHolidays = (departmentId: string, range: ScheduleRange): ScheduleHolidays => {
	const { t } = useTranslation();
	const { trackError } = useAnalytics();
	const enabled = useCanSeeScheduleHolidays() && range !== null;
	// A holiday's hours can start the evening before its date or end the next day, so shifts at the range's edges still find it.
	const holidaysQuery = useHolidays(departmentId, (range?.from ?? UNLOADED_DAY).minus({ days: 1 }), (range?.to ?? UNLOADED_DAY).plus({ days: 1 }), {
		enabled,
	});
	const groupsQuery = useHolidayGroups(departmentId, { enabled });
	const isError = (holidaysQuery.isError && holidaysQuery.data === undefined) || (groupsQuery.isError && groupsQuery.data === undefined);
	useEffect(() => {
		if (isError) trackError("schedule_holidays_load_error", { department_id: departmentId });
	}, [isError, trackError, departmentId]);
	return useMemo(() => {
		// DEFAULT: until both load, when either fails, or for users who can't see them, no day is marked and the schedule
		// shows as usual. Only the Day view warns that the load failed; the week views stay silent.
		const holidays = holidaysQuery.data === undefined || groupsQuery.data === undefined ? [] : toScheduleHolidays(t, holidaysQuery.data, groupsQuery.data);
		const byDate = new Map(holidays.map((scheduleHoliday) => [scheduleHoliday.holiday.date.toFormat(ISO_DATE), scheduleHoliday]));
		return { holidays, holidayOn: (date: DateTime) => byDate.get(date.toFormat(ISO_DATE)), isError };
	}, [t, holidaysQuery.data, groupsQuery.data, isError]);
};
